import { axiosInstance } from "@/pages/auth/auth.context";
import {
  Document,
  DocumentAssignment,
  CreateDocumentAssignmentRequest,
  DocumentTemplate,
  DashboardStatistics,
  BulkAssignmentRequest,
  BulkStatusUpdateRequest,
  DocumentFilters,
  AssignmentFilters,
  PaginatedResponse,
  SignatureData,
  UserCompletionStats,
  DocumentCompletionStats,
} from "@/types/document";

// Document CRUD Operations
export const documentService = {
  getDocuments: async (
    filters: DocumentFilters = {},
  ): Promise<PaginatedResponse<Document>> => {
    const params = new URLSearchParams();
    if (filters.page !== undefined)
      params.append("page", filters.page.toString());
    if (filters.size !== undefined)
      params.append("size", filters.size.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.type) params.append("type", filters.type);
    if (filters.positionId) params.append("positionId", filters.positionId);
    if (filters.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());

    const response = await axiosInstance.get(`documents?${params}`);
    return response.data;
  },

  // Get single document
  getDocument: async (id: string): Promise<Document> => {
    const response = await axiosInstance.get(`documents/${id}`);
    return response.data;
  },

  createDocument: async (formData: FormData): Promise<Document> => {
    const response = await axiosInstance.post(`documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  updateDocument: async (
    id: string,
    document: Partial<Document>,
  ): Promise<Document> => {
    const response = await axiosInstance.put(`documents/${id}`, document);
    return response.data;
  },

  deleteDocument: async (id: string): Promise<void> => {
    await axiosInstance.delete(`documents/${id}`);
  },

  downloadDocument: async (id: string): Promise<Blob> => {
    const response = await axiosInstance.get(`documents/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },
};

// Document Assignment Operations
export const assignmentService = {
  getAssignments: async (
    filters: AssignmentFilters = {},
  ): Promise<PaginatedResponse<DocumentAssignment>> => {
    const params = new URLSearchParams();
    if (filters.page !== undefined)
      params.append("page", filters.page.toString());
    if (filters.size !== undefined)
      params.append("size", filters.size.toString());

    // Handle multiple statuses
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => params.append("statuses", status));
    } else if (filters.status) {
      params.append("statuses", filters.status);
    }

    // Handle multiple userIds
    if (filters.userIds && filters.userIds.length > 0) {
      filters.userIds.forEach((userId) => params.append("userIds", userId));
    } else if (filters.userId) {
      params.append("userIds", filters.userId);
    }

    // Handle multiple documentIds
    if (filters.documentIds && filters.documentIds.length > 0) {
      filters.documentIds.forEach((documentId) =>
        params.append("documentIds", documentId),
      );
    } else if (filters.documentId) {
      params.append("documentIds", filters.documentId);
    }

    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.myDocumentsOnly)
      params.append("myDocumentsOnly", filters.myDocumentsOnly.toString());

    const response = await axiosInstance.get(`document-assignments?${params}`);
    return response.data;
  },

  // Get single assignment
  getAssignment: async (id: string): Promise<DocumentAssignment> => {
    const response = await axiosInstance.get(`document-assignments/${id}`);
    return response.data;
  },

  createAssignment: async (
    assignment: CreateDocumentAssignmentRequest,
  ): Promise<DocumentAssignment> => {
    const response = await axiosInstance.post(
      `document-assignments`,
      assignment,
    );
    return response.data;
  },

  updateAssignment: async (
    id: string,
    assignment: Partial<DocumentAssignment>,
  ): Promise<DocumentAssignment> => {
    const response = await axiosInstance.put(
      `document-assignments/${id}`,
      assignment,
    );
    return response.data;
  },

  deleteAssignment: async (id: string): Promise<void> => {
    await axiosInstance.delete(`document-assignments/${id}`);
  },

  signDocument: async (
    id: string,
    signatureData?: SignatureData,
  ): Promise<DocumentAssignment> => {
    const response = await axiosInstance.post(
      `document-assignments/${id}/sign`,
      signatureData,
    );
    return response.data;
  },
};

// Reporting Operations
export const reportService = {
  getDashboardStatistics: async (): Promise<DashboardStatistics> => {
    const response = await axiosInstance.get(
      `document-reports/dashboard-statistics`,
    );
    return response.data;
  },

  // Get user completion statistics
  getUserCompletionStats: async (
    userId: string,
  ): Promise<UserCompletionStats> => {
    const response = await axiosInstance.get(
      `document-reports/user-completion/${userId}`,
    );
    return response.data;
  },

  // Get document completion statistics
  getDocumentCompletionStats: async (
    docId: string,
  ): Promise<DocumentCompletionStats> => {
    const response = await axiosInstance.get(
      `document-reports/document-completion/${docId}`,
    );
    return response.data;
  },

  // Reporting and Analytics
  getReportingDashboardStatistics: async () => {
    const response = await axiosInstance.get("reports/dashboard");
    return response.data;
  },

  getCompletionData: async (timeRange: "7d" | "30d" | "90d" | "1y") => {
    const response = await axiosInstance.get(
      `reports/completion?timeRange=${timeRange}`,
    );
    return response.data;
  },

  getCategoryData: async () => {
    const response = await axiosInstance.get("reports/categories");
    return response.data;
  },

  getRecentActivity: async (
    type?: string,
    timeRange?: "1h" | "24h" | "7d" | "30d",
    page = 1,
    limit = 20,
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (type) params.append("type", type);
    if (timeRange) params.append("timeRange", timeRange);

    const response = await axiosInstance.get(`reports/activity?${params}`);
    return response.data;
  },

  getPerformanceMetrics: async (timeRange: "7d" | "30d" | "90d" | "1y") => {
    const response = await axiosInstance.get(
      `reports/performance?timeRange=${timeRange}`,
    );
    return response.data;
  },

  getUserPerformance: async (timeRange: "7d" | "30d" | "90d" | "1y") => {
    const response = await axiosInstance.get(
      `reports/user-performance?timeRange=${timeRange}`,
    );
    return response.data;
  },

  getDepartmentMetrics: async (timeRange: "7d" | "30d" | "90d" | "1y") => {
    const response = await axiosInstance.get(
      `reports/department-metrics?timeRange=${timeRange}`,
    );
    return response.data;
  },

  getTrendData: async (timeRange: "7d" | "30d" | "90d" | "1y") => {
    const response = await axiosInstance.get(
      `reports/trends?timeRange=${timeRange}`,
    );
    return response.data;
  },

  exportDashboardData: async (format: "pdf" | "excel" | "csv") => {
    const response = await axiosInstance.get(`reports/export/dashboard`, {
      params: { format },
      responseType: "blob",
    });
    return response.data;
  },

  exportCustomReport: async (
    reportConfig: Record<string, unknown>,
    format: "pdf" | "excel" | "csv",
  ) => {
    const response = await axiosInstance.post(
      `reports/export/custom`,
      reportConfig,
      {
        params: { format },
        responseType: "blob",
      },
    );
    return response.data;
  },

  sendReminder: async (assignmentId: string) => {
    const response = await axiosInstance.post(
      `assignments/${assignmentId}/remind`,
    );
    return response.data;
  },

  escalateAssignment: async (assignmentId: string) => {
    const response = await axiosInstance.post(
      `assignments/${assignmentId}/escalate`,
    );
    return response.data;
  },

  bulkSendReminders: async (assignmentIds: string[]) => {
    const response = await axiosInstance.post("assignments/bulk-remind", {
      assignmentIds,
    });
    return response.data;
  },

  bulkEscalateAssignments: async (assignmentIds: string[]) => {
    const response = await axiosInstance.post("assignments/bulk-escalate", {
      assignmentIds,
    });
    return response.data;
  },

  createCustomReport: async (reportConfig: Record<string, unknown>) => {
    const response = await axiosInstance.post("reports/custom", reportConfig);
    return response.data;
  },

  getCustomReports: async () => {
    const response = await axiosInstance.get("reports/custom");
    return response.data;
  },

  deleteCustomReport: async (reportId: string) => {
    const response = await axiosInstance.delete(`reports/custom/${reportId}`);
    return response.data;
  },

  // ---- Custom reports ----
  generateCustomReport: async (reportConfig: Record<string, unknown>) => {
    const response = await axiosInstance.post(
      "reports/custom/generate",
      reportConfig,
    );
    return response.data;
  },

  scheduleReport: async (
    reportId: string,
    schedule: Record<string, unknown>,
  ) => {
    const response = await axiosInstance.post(
      `reports/custom/${reportId}/schedule`,
      schedule,
    );
    return response.data;
  },

  getReportFields: async () => {
    const response = await axiosInstance.get("reports/fields");
    return response.data;
  },
};

// Helper function to handle file downloads
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
